
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, Users, Link as LinkIcon, DollarSign } from 'lucide-react';
import { SubscriptionPlan } from '@/types/subscriptionPlans';

interface PlanPreviewCardProps {
  plan: SubscriptionPlan;
}

const PlanPreviewCard: React.FC<PlanPreviewCardProps> = ({ plan }) => {
  // Determine card colors based on plan ID
  const getCardColors = (planId: string) => {
    switch (planId) {
      case 'starter':
        return {
          borderColor: 'border-blue-200',
          gradient: 'bg-gradient-to-b from-white to-blue-50',
          title: 'text-blue-700',
          badge: 'bg-blue-500',
        };
      case 'growth':
        return {
          borderColor: 'border-green-200',
          gradient: 'bg-gradient-to-b from-white to-green-50',
          title: 'text-green-700',
          badge: 'bg-green-500',
        };
      case 'professional':
        return {
          borderColor: 'border-purple-200',
          gradient: 'bg-gradient-to-b from-white to-purple-50',
          title: 'text-purple-700',
          badge: 'bg-purple-500',
        };
      case 'enterprise':
        return {
          borderColor: 'border-indigo-200',
          gradient: 'bg-gradient-to-b from-white to-indigo-50',
          title: 'text-indigo-700',
          badge: 'bg-indigo-500',
        };
      default:
        return {
          borderColor: 'border-gray-200',
          gradient: 'bg-gradient-to-b from-white to-gray-50',
          title: 'text-gray-700',
          badge: 'bg-gray-500',
        };
    }
  };

  const colors = getCardColors(plan.id);

  return (
    <Card className={`relative border-2 ${colors.borderColor} hover:shadow-lg transition-all ${colors.gradient} transform hover:scale-105 duration-300`}>
      {plan.recommended && (
        <div className={`absolute top-0 right-0 ${colors.badge} text-white px-3 py-1 text-xs font-semibold rounded-bl-md rounded-tr-md shadow-sm`}>
          {plan.badgeText || 'RECOMMENDED'}
        </div>
      )}
      <CardHeader className="text-center pb-4">
        <CardTitle className={`text-xl ${colors.title}`}>{plan.name}</CardTitle>
        <div className="mt-4 space-y-1">
          <div>
            <span className="text-3xl font-bold">${plan.monthlyPrice}</span>
            <span className="text-lg">/month</span>
          </div>
          <div className="text-sm text-green-600">
            ${plan.quarterlyPrice}/quarter (save {plan.quarterlyDiscount}%)
          </div>
        </div>
        <CardDescription className="mt-2">
          {plan.description}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <ul className="space-y-3">
            {plan.features.map((feature, index) => (
              <li key={index} className="flex items-start">
                {feature.included ? (
                  <Check className="h-5 w-5 text-green-500 mr-2 shrink-0" />
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5 text-red-500 mr-2 shrink-0"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                )}
                <span className="text-sm">{feature.name}</span>
              </li>
            ))}
          </ul>
          
          <div className="border-t pt-3 mt-3">
            <h4 className="text-sm font-medium mb-2">Limits:</h4>
            <div className="space-y-2">
              <div className="flex items-center">
                <Users className="h-4 w-4 text-blue-500 mr-2" />
                <span className="text-sm">Up to {plan.maxReferrals} referrals</span>
              </div>
              <div className="flex items-center">
                <Users className="h-4 w-4 text-blue-500 mr-2" />
                <span className="text-sm">Up to {plan.maxManagers} managers</span>
              </div>
              <div className="flex items-center">
                <LinkIcon className="h-4 w-4 text-blue-500 mr-2" />
                <span className="text-sm">Up to {plan.maxReferralLinks} referral links</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PlanPreviewCard;
